import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import CustomDateRangePicker from "components/ui/forms/DateRangePicker";

const DateFilter = ({ setShowReportData, setSelectedDate, selectedDate, setCustomDate, title }) => {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [chosenDate, setChosenDate] = useState(""); // display-only string
  const [dateRangeState, setDateRangeState] = useState([
    { startDate: new Date(), endDate: null, key: "selection" },
  ]);



  // LIVE update handler from the picker
  const handleRangeSelectedLive = ({ startDate, endDate }) => {
    // 1) update display label
    const label = `${format(startDate, "dd-MM-yyyy")} - ${format(endDate, "dd-MM-yyyy")}`;
    setChosenDate(label);

    // 2) update parent-facing customDate (keep object shape)
    setCustomDate({
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
    });
  };

  const handleCloseDate = () => {
    // If you want Close to only close the picker (since we already set values live), do this:
    setIsDatePickerOpen(false);
    setShowReportData(false);

    // If you prefer to keep the old fallback, you can keep this block —
    // it's redundant now, but harmless:
    // if (dateRangeState[0]?.endDate) {
    //   const formattedRange = `${format(dateRangeState[0]?.startDate, "dd-MM-yyyy")} - ${format(dateRangeState[0]?.endDate, "dd-MM-yyyy")}`;
    //   setChosenDate(formattedRange);
    //   setCustomDate({
    //     startDate: moment(dateRangeState[0]?.startDate).format("YYYY-MM-DD"),
    //     endDate: moment(dateRangeState[0]?.endDate).format("YYYY-MM-DD"),
    //   });
    // }
  };

  const handleClearDate = () => {
    setDateRangeState([{ startDate: new Date(), endDate: null, key: "selection" }]);
    setChosenDate("");
    setSelectedDate("");
    // keep object shape to avoid undefined checks upstream
    setCustomDate({ startDate: "", endDate: "" });
  };

  const dateOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "7days" },
    { label: "This Month", value: "month" },
    {
      label: (
        <span className="flex justify-between items-center">
          Custom: Choose Date <i className="fa fa-calendar" aria-hidden="true"></i>
        </span>
      ),
      value: "custom",
    },
  ];

  return (
    <>
      <div className="mb-4">
        <label className="block font-normal ml-2 text-gray-800 text-[17px]">{title}</label>
        <div className="flex flex-wrap gap-1 md:gap-0 md:w-full">
          {dateOptions.map((option, index) => (
            <label
              key={option.value}
              className={`flex md:flex-1 md:w-full md:min-w-[15%] md:last:min-w-[30%] md:gap-3 items-center ml-0.5 p-2.5 cursor-pointer first:rounded-tl-lg first:rounded-bl-lg last:rounded-tr-lg last:rounded-br-lg rounded-lg md:rounded-none
                ${selectedDate === option.value ? "bg-primary/70 text-black shadow-md" : "bg-[#e2e2e2] text-gray-700"}
                ${index !== 4 ? "w-[calc(50%-.25rem)] gap-1" : "w-full"}`}
              onClick={(e) => {
                if (option.value === "custom") {
                  e.preventDefault();
                  setSelectedDate("custom");
                  setIsDatePickerOpen((v) => !v);
                } else {
                  setSelectedDate(option.value);
                  setIsDatePickerOpen(false);
                  setChosenDate(""); // clear custom label
                  setCustomDate({ startDate: "", endDate: "" }); // clear custom values
                }
              }}
            >
              <input
                type="radio"
                name="transactionDate"
                value={option.value}
                checked={selectedDate === option.value}
                onChange={() => setSelectedDate(option.value)}
                className="form-radio h-3 w-3"
              />
              <span className="font-bold text-sm w-full">
                {selectedDate === "custom" && option.value === "custom" && chosenDate
                  ? <span>Custom: {chosenDate}</span>
                  : option.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isDatePickerOpen && (
          <div className="mb-8">
            <motion.div initial={{ height: 0 }} animate={{ height: 1 }} exit={{ height: 0 }} key="slide" />
            <div className="flex flex-row justify-center">
              <CustomDateRangePicker
                dateRangeState={dateRangeState}
                setDateRangeState={setDateRangeState}
                onRangeSelected={handleRangeSelectedLive} // <-- NEW: live updates
              />
            </div>

            <div className="flex justify-center mt-4 space-x-4">
              <button
                className="bg-primary hover:bg-primary/80 text-black font-bold p-2 min-w-[150px] rounded-lg disabled:opacity-50"
                disabled={!dateRangeState[0].endDate}
                onClick={handleCloseDate}
              >
                {t("CLOSE")}
              </button>
              <button
                className="border border-black hover:bg-gray-300 text-gray-700 font-bold p-2 min-w-[150px] rounded-lg disabled:opacity-50"
                onClick={handleClearDate}
              >
                {t("CLEAR")}
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DateFilter;
